/* eslint-disable react-hooks/exhaustive-deps */
import {
  Button,
  DatePicker,
  Dropdown,
  Modal,
  Radio,
  Select,
  Table,
  Tooltip,
  message,
} from "antd";
import { useEffect, useState } from "react";
import useGroups from "../hooks/useGroups";
import useCompanies from "../hooks/useCompanies";
import dayjs from "dayjs";
import {
  FilePdfOutlined,
  WhatsAppOutlined,
  MoonOutlined,
  FileExcelOutlined,
} from "@ant-design/icons";
import { BookUser, Settings, Trash2, X } from "lucide-react";

// Opciones de filtro de fecha
const DATE_FILTER_OPTIONS = [
  { key: "today", label: "Hoy", getRange: () => [dayjs(), dayjs()] },
  { key: "week_back", label: "1 semana atrás", getRange: () => [dayjs().subtract(7, "day"), dayjs()] },
  { key: "month_back", label: "1 mes atrás", getRange: () => [dayjs().subtract(1, "month"), dayjs()] },
  { key: "week_forward", label: "1 semana adelante", getRange: () => [dayjs(), dayjs().add(7, "day")] },
  { key: "month_forward", label: "1 mes adelante", getRange: () => [dayjs(), dayjs().add(1, "month")] },
  { key: "custom", label: "Personalizado", getRange: null },
];

function GroupTable({ onModeSet, onGroupClick }) {
  const [params, setParams] = useState({
    page: 1,
    check_in_date_range_before: dayjs().format("YYYY-MM-DD"),
    check_in_date_range_after: dayjs().format("YYYY-MM-DD"),
  });
  const {
    groups,
    getGroups,
    deleteGroup,
    setExcursionToGroup,
    unsetExcursionFromGroup,
    loading: groupsLoading,
  } = useGroups();
  const [localGroups, setLocalGroups] = useState({ results: [], count: 0 });
  const { companies, getCompanies } = useCompanies();
  const [selectedDateFilter, setSelectedDateFilter] = useState("today");
  const [customDateRange, setCustomDateRange] = useState(null);
  const [checkOutDateRange, setCheckOutDateRange] = useState(null);
  const [selectedCompanies, setSelectedCompanies] = useState([]);
  const [companyFilterLoading, setCompanyFilterLoading] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);

  useEffect(() => {
    getGroups(params);
  }, [params]);

  useEffect(() => {
    if (groups && groups.results) {
      setLocalGroups(groups);
    }
  }, [groups]);

  useEffect(() => {
    // Load companies when component mounts
    const loadCompanies = async () => {
      setCompanyFilterLoading(true);
      try {
        await getCompanies();
        console.log("Companies data:", companies); // Debug log to check the structure
      } catch (error) {
        console.error("Failed to load companies", error);
      } finally {
        setCompanyFilterLoading(false);
      }
    };

    loadCompanies();
  }, []);

  const handleDeleteGroup = async (group) => {
    await deleteGroup(group.id);
    getGroups(params);
  };

  const handleDateFilterChange = (filterKey) => {
    setSelectedDateFilter(filterKey);
    const option = DATE_FILTER_OPTIONS.find((opt) => opt.key === filterKey);
    if (option && option.getRange) {
      const [start, end] = option.getRange();
      setParams({
        ...params,
        check_in_date_range_after: start.format("YYYY-MM-DD"),
        check_in_date_range_before: end.format("YYYY-MM-DD"),
        page: 1,
      });
    }
    // Si es custom, no hacemos nada hasta que seleccione el rango
  };

  const handleCustomDateRangeChange = (dates) => {
    if (!dates || !dates[0] || !dates[1]) {
      setCustomDateRange(null);
      return;
    }
    setCustomDateRange(dates);
    setParams({
      ...params,
      check_in_date_range_after: dates[0].format("YYYY-MM-DD"),
      check_in_date_range_before: dates[1].format("YYYY-MM-DD"),
      page: 1,
    });
  };

  const handleCheckOutDateRangeChange = (dates) => {
    if (!dates || !dates[0] || !dates[1]) {
      // If dates are cleared, reset the filter
      setCheckOutDateRange(null);
      const {
        check_out_date_range_after,
        check_out_date_range_before,
        ...restParams
      } = params;
      setParams({
        ...restParams,
        page: 1, // Reset to first page when filter changes
      });
      return;
    }

    setCheckOutDateRange(dates);
    setParams({
      ...params,
      check_out_date_range_after: dates[0].format("YYYY-MM-DD"),
      check_out_date_range_before: dates[1].format("YYYY-MM-DD"),
      page: 1, // Reset to first page when filter changes
    });
  };

  const handleCompanyFilterChange = (selectedValues) => {
    setSelectedCompanies(selectedValues);

    if (selectedValues.length === 0) {
      // If no companies selected, remove company filter
      const { company, ...restParams } = params;
      setParams({
        ...restParams,
        page: 1,
      });
    } else {
      // Apply company filter
      setParams({
        ...params,
        company: selectedValues.join(","),
        page: 1,
      });
    }
  };

  // Check if there are any active filters (besides the default "today")
  const hasActiveFilters = () => {
    return (
      selectedDateFilter !== "today" ||
      checkOutDateRange !== null ||
      selectedCompanies.length > 0
    );
  };

  // Clear all filters function
  const clearAllFilters = () => {
    setCheckOutDateRange(null);
    setSelectedCompanies([]);
    setSelectedDateFilter("today");
    setCustomDateRange(null);

    const today = dayjs();
    const {
      check_out_date_range_before,
      check_out_date_range_after,
      company,
      ...restParams
    } = params;

    setParams({
      ...restParams,
      check_in_date_range_before: today.format("YYYY-MM-DD"),
      check_in_date_range_after: today.format("YYYY-MM-DD"),
      page: 1,
    });
  };

  // Export to Excel function
  const handleExportToExcel = async () => {
    if (!params.check_in_date_range_after) {
      message.error("Por favor selecciona una fecha de entrada");
      return;
    }

    setExportLoading(true);
    try {
      const firstDate = params.check_in_date_range_after;
      const endpoint = `groups/daily-groups-excel/?date=${firstDate}`;
      
      const baseUrl = process.env.REACT_APP_API_URL;
      let accessToken = localStorage.getItem("accessToken");

      const response = await fetch(`${baseUrl}${endpoint}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${accessToken}`,
        },
      });

      if (!response.ok) {
        throw new Error('Error al descargar el archivo');
      }

      // Get the blob from the response
      const blob = await response.blob();
      
      // Create a download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `grupos_${firstDate}.xlsx`;
      document.body.appendChild(link);
      link.click();
      
      // Cleanup
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      message.success("Archivo descargado exitosamente");
    } catch (error) {
      console.error("Error exporting to Excel:", error);
      message.error("Error al exportar a Excel");
    } finally {
      setExportLoading(false);
    }
  };

  // Function to handle excursion selection change
  const handleExcursionSelectionChange = async (
    selectedExcursionIds,
    record
  ) => {
    try {
      // Create a deep copy of the local groups data
      const updatedGroups = JSON.parse(JSON.stringify(localGroups));

      // Find the record in the results array
      const groupIndex = updatedGroups.results.findIndex(
        (group) => group.id === record.id
      );

      if (groupIndex !== -1) {
        const currentGroup = updatedGroups.results[groupIndex];
        const currentExcursions = currentGroup.scheduledExcursions || [];

        // Find which excursions were previously selected
        const currentSelectedIds = currentExcursions
          .filter((ex) => ex.isScheduleForGroup)
          .map((ex) => ex.id);

        // Process each newly selected excursion
        for (const excursionId of selectedExcursionIds) {
          if (!currentSelectedIds.includes(excursionId)) {
            // This is a newly added excursion
            await setExcursionToGroup(record.id, excursionId);
          }
        }

        // Process each removed excursion
        for (const excursionId of currentSelectedIds) {
          if (!selectedExcursionIds.includes(excursionId)) {
            // This excursion was removed, unset it from the group
            await unsetExcursionFromGroup(record.id, excursionId);
          }
        }

        // Update local state for immediate UI feedback
        currentGroup.scheduledExcursions = currentExcursions.map((ex) => ({
          ...ex,
          isScheduleForGroup: selectedExcursionIds.includes(ex.id),
        }));

        // Update the local state to trigger re-render
        setLocalGroups(updatedGroups);

        // Refresh the groups data to ensure we have the latest state from the backend
        await getGroups(params);
      }
    } catch (error) {
      console.error("Error updating excursions:", error);
      message.error("Error al actualizar las excursiones");
      // Refresh the groups data to ensure we have the correct state
      await getGroups(params);
    }
  };

  const columns = [
    {
      title: "Empresa",
      dataIndex: ["company", "name"],
      key: "company",
      fixed: "left",
      width: 150,
      filterDropdown: () => (
        <div style={{ padding: 8, minWidth: 280 }}>
          <span className="font-semibold">Filtrar por empresas</span>
          <Select
            mode="multiple"
            allowClear
            showSearch
            placeholder="Selecciona empresas"
            value={selectedCompanies}
            onChange={handleCompanyFilterChange}
            style={{ width: "100%", marginTop: 8 }}
            loading={companyFilterLoading}
            options={
              Array.isArray(companies)
                ? companies.map((company) => ({
                    value: company.id,
                    label: company.name,
                  }))
                : companies.results && Array.isArray(companies.results)
                ? companies.results.map((company) => ({
                    value: company.id,
                    label: company.name,
                  }))
                : []
            }
            filterOption={(input, option) =>
              (option?.label ?? "").toLowerCase().includes(input.toLowerCase())
            }
          />
        </div>
      ),
      filtered: selectedCompanies.length > 0,
    },
    {
      title: "Entrada",
      dataIndex: "checkInDate",
      key: "checkInDate",
      fixed: "left",
      width: 120,
    },
    {
      title: "Salida",
      dataIndex: "checkOutDate",
      key: "checkOutDate",
      width: 120,
      filterDropdown: () => (
        <div style={{ padding: 8, minWidth: 280 }}>
          <span className="font-semibold">Selecciona un rango de fecha</span>
          <DatePicker.RangePicker
            value={checkOutDateRange}
            onChange={handleCheckOutDateRangeChange}
            style={{ width: "100%" }}
          />
        </div>
      ),
      filtered: checkOutDateRange !== null,
      render: (text, record) => {
        // Calculate nights between check-in and check-out dates
        const checkInDate = dayjs(record.checkInDate);
        const checkOutDate = dayjs(record.checkOutDate);
        const nights = checkOutDate.diff(checkInDate, "day");

        return (
          <div>
            <div>{text}</div> {/* Ensure a value is always shown */}
            <div style={{ fontSize: "12px", color: "#8c8c8c" }}>
              <MoonOutlined style={{ fontSize: "14px", marginRight: "4px" }} />
              {nights} {nights === 1 ? "noche" : "noches"}
            </div>
          </div>
        );
      },
    },
    {
      title: "Coordinador",
      dataIndex: ["coordinator", "firstName"],
      key: "coordinator",
      width: 140,
      render: (text, record) => {
        const phoneNumber = record.coordinator?.phoneNumber;
        const isValidPhoneNumber =
          phoneNumber && /^\+?[1-9]\d{1,14}$/.test(phoneNumber);
        return isValidPhoneNumber ? (
          <Tooltip title="Click para abrir en WhatsApp">
            <a
              href={`https://wa.me/${phoneNumber}`}
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: "#25D366" }}
            >
              <WhatsAppOutlined
                style={{ fontSize: "12px", marginRight: "4px" }}
              />
              {text}
            </a>
          </Tooltip>
        ) : (
          text
        );
      },
    },
    {
      title: "Rebotero",
      dataIndex: "entryCoordinatorName",
      key: "entryCoordinatorName",
      width: 140,
      render: (text, record) => {
        const entryNumber = record.entryNumber;
        const isValidPhoneNumber =
          entryNumber && /^\+?[1-9]\d{1,14}$/.test(entryNumber);
        return isValidPhoneNumber ? (
          <Tooltip title="click para abrir en whatsapp">
            <a
              href={`https://wa.me/${entryNumber}`}
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: "#25D366" }}
            >
              <WhatsAppOutlined
                style={{ fontSize: "12px", marginRight: "4px" }}
              />
              {text}
            </a>
          </Tooltip>
        ) : (
          text
        );
      },
    },
    {
      title: "Observación",
      dataIndex: "observation",
      key: "observation",
      width: 150,
    },
    {
      title: "Excursiones",
      dataIndex: "scheduledExcursions",
      key: "scheduledExcursions",
      minWidth: 300, // Set minimum width
      render: (_, record) => {
        const currentGroup = localGroups.results?.find(
          (g) => g.id === record.id
        );
        const excursions = currentGroup?.scheduledExcursions || [];

        if (excursions.length === 0) {
          return <span className="text-gray-400">No hay excursiones</span>;
        }

        // Only select excursions with isScheduleForGroup === true
        const selectedExcursionIds = excursions
          .filter((ex) => ex.isScheduleForGroup === true)
          .map((ex) => ex.id);

        return (
          <Select
            mode="multiple"
            value={selectedExcursionIds}
            options={excursions.map((ex) => ({
              label: `${dayjs(ex.day).format("ddd DD/MM")}: ${
                ex.excursionType.name
              } `,
              value: ex.id,
            }))}
            onChange={(newSelectedIds) =>
              handleExcursionSelectionChange(newSelectedIds, record)
            }
            showSearch={false}
            popupMatchSelectWidth={false}
            dropdownStyle={{ minWidth: "250px" }}
            tagRender={(props) => {
              const { label, value, closable, onClose } = props;
              const excursion = excursions.find((ex) => ex.id === value);
              if (!excursion) return null;

              // Format the date for display
              const formattedDate = dayjs(excursion.day).format("ddd DD/MM");

              return (
                <Tooltip
                  title={`${dayjs(excursion.day).format(
                    "DD/MM/YYYY"
                  )} - Estado: ${excursion.status}`}
                >
                  <span
                    className="inline-flex items-center px-2 py-1 m-1 bg-blue-100 text-blue-800 rounded"
                    style={{
                      maxWidth: "100%",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    <span className="font-medium mr-1">{formattedDate}:</span>
                    {excursion.excursionType.name}
                    {closable && (
                      <span
                        className="ml-1 cursor-pointer text-blue-600 hover:text-blue-800"
                        onClick={(e) => {
                          e.stopPropagation();
                          onClose();
                        }}
                      >
                        ×
                      </span>
                    )}
                  </span>
                </Tooltip>
              );
            }}
            listHeight={200}
            placement="bottomLeft"
            placeholder="Seleccionar excursiones"
            style={{ width: "100%" }}
          />
        );
      },
    },
    {
      title: "",
      key: "actions",
      width: 80,
      fixed: "right", // Fix the actions column to the right
      render: (text, record) => {
        const items = [
          {
            key: "company",
            label: (
              <div
                className="flex items-center"
                onClick={() => {
                  window.open(
                    `${process.env.REACT_APP_API_URL}groups/company-commission/${record.id}/`,
                    "_blank"
                  );
                }}
              >
                <FilePdfOutlined className="me-2" />

                <span>Descargar rendición de empresa</span>
              </div>
            ),
          },
          {
            key: "coordinator",
            label: (
              <div
                className="flex items-center"
                onClick={() => {
                  window.open(
                    `${process.env.REACT_APP_API_URL}groups/coordinator-commission/${record.id}/`,
                    "_blank"
                  );
                }}
              >
                <FilePdfOutlined className="me-2" />

                <span>Descargar rendición de coordinador</span>
              </div>
            ),
          },
          {
            key: "coordinatorPedraza",
            label: (
              <div
                className="flex items-center"
                onClick={() => {
                  window.open(
                    `${process.env.REACT_APP_API_URL}groups/export-commissions-excel/${record.id}/`,
                    "_blank"
                  );
                }}
              >
                <FilePdfOutlined className="me-2" />
                <span>Descargar rendición mensual de coord/empresa Excel (formato Pedraza/Amcabi)</span>
              </div>
            ),
          },
          {
            key: "companyMonthly",
            label: (
              <div
                className="flex items-center"
                onClick={() => {
                  window.open(
                    `${process.env.REACT_APP_API_URL}groups/company-monthly-commission/${record.id}/`,
                    "_blank"
                  );
                }}
              >
                <FilePdfOutlined className="me-2" />
                <span>Descargar rendición mensual de empresas PDF</span>
              </div>
            ),
          },
          {
            key: "sales",
            label: (
              <div
                className="flex items-center"
                onClick={() => {
                  window.open(
                    `${process.env.REACT_APP_API_URL}admin/sellers/passenger/?group__pk__exact=${record.id}`,
                    "_blank"
                  );
                }}
              >
                <BookUser size={16} className="me-2" />

                <span>Ver ventas</span>
              </div>
            ),
          },
          {
            key: "delete",
            danger: true,
            label: (
              <div
                className="flex items-center"
                onClick={async () => {
                  Modal.confirm({
                    title: "Borrar grupo",
                    content: "¿Estás seguro que quieres borrar el grupo?",
                    onOk: () => {
                      handleDeleteGroup(record);
                    },
                  });
                }}
              >
                <Trash2 size={16} className="me-2" />
                <span>Borrar</span>
              </div>
            ),
          },
        ];

        return (
          <Dropdown menu={{ items }} trigger={["click"]}>
            <Button type="text" icon={<Settings size={20} />} />
          </Dropdown>
        );
      },
    },
  ];

  const handleTableChange = (pagination) => {
    const newParams = { ...params, page: pagination };
    setParams(newParams);
  };

  return (
    <div className="">
      <div className="flex justify-between mb-3">
        <span className="font-semibold text-lg">Listado de grupos</span>

        <div className="flex gap-2">
          {hasActiveFilters() && (
            <Button onClick={clearAllFilters} icon={<X size={16} />}>
              Limpiar filtros
            </Button>
          )}
          <Button
            onClick={handleExportToExcel}
            loading={exportLoading}
            icon={<FileExcelOutlined />}
          >
            Exportar a Excel por f. ingreso
          </Button>
          <Button
            type="primary"
            onClick={() => {
              onModeSet("creation");
            }}
          >
            Crear Grupo
          </Button>
        </div>
      </div>

      {/* Filtro de fecha de entrada */}
      <div className="mb-3 flex items-center gap-3 flex-wrap">
        <span className="text-gray-500 text-sm">Fecha de entrada:</span>
        <Radio.Group
          value={selectedDateFilter}
          onChange={(e) => handleDateFilterChange(e.target.value)}
          optionType="button"
          buttonStyle="solid"
        >
          {DATE_FILTER_OPTIONS.map((option) => (
            <Radio.Button key={option.key} value={option.key}>
              {option.label}
            </Radio.Button>
          ))}
        </Radio.Group>
        {selectedDateFilter === "custom" && (
          <DatePicker.RangePicker
            value={customDateRange}
            onChange={handleCustomDateRangeChange}
            format="DD/MM/YYYY"
            placeholder={["Desde", "Hasta"]}
          />
        )}
      </div>

      <Table
        loading={groupsLoading.get}
        columns={columns}
        dataSource={localGroups.results}
        pagination={{
          current: params.page,
          pageSize: localGroups.results?.length,
          total: localGroups.count,
          onChange: handleTableChange,
        }}
        rowKey="id"
        size="small"
        scroll={{ x: "max-content" }}
        onRow={(record) => ({
          onClick: (e) => {
            // No abrir modal si se hizo click en un select, dropdown o botón
            if (
              e.target.closest('.ant-select') ||
              e.target.closest('.ant-dropdown-trigger') ||
              e.target.closest('button') ||
              e.target.closest('a')
            ) {
              return;
            }
            if (onGroupClick) {
              onGroupClick(record);
            }
          },
          style: { cursor: 'pointer' },
        })}
      />
    </div>
  );
}

export default GroupTable;
